import Title from "../shared/components/Title";
import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import UploadedItemCard from "../features/customer-rate-generator/components/UploadedItemCard";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import OverlayLoading from "../shared/components/OverlayLoading";
import DropzoneComponent from "../shared/components/DropzoneComponent";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faTrash,
  faCheckCircle,
  faExclamationTriangle,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import apiClient from "../utils/apiClient";

const CustomerRateGenerator = () => {
  const [file, setFile] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState([]);
  const [accountDetails, setAccountDetails] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onDrop = useCallback(async (acceptedFile) => {
    setIsSubmitting(true);

    try {
      const newFile = await Promise.all(
        acceptedFile.map(async (file) => {
          const originalName = file.name;
          const fileExtension = originalName.split(".").pop();
          const fileName = originalName.replace(`.${fileExtension}`, "");

          const now = new Date();
          const timestamp = now
            .toISOString()
            .replace(/[-:]/g, "")
            .replace("T", "_")
            .split(".")[0];
          const newFileName = `${fileName}_${timestamp}.${fileExtension}`;

          let sheetList = [];
          if (
            fileExtension === "xlsx" ||
            fileExtension === "xlsb" ||
            fileExtension === "xlsm"
          ) {
            try {
              const data = await file.arrayBuffer();
              const workbook = XLSX.read(data, { type: "array" });
              sheetList = workbook.SheetNames;
            } catch (error) {
              console.error("Error reading Excel file:", error);
              toast.error(`Error reading ${file.name}: ${error.message}`);
            }
          }

          return {
            originalFile: file,
            renamedFile: new File([file], newFileName, { type: file.type }),
            sheetList,
          };
        })
      );

      setFile((prev) => [...prev, ...newFile]);
      toast.success(`${newFile.length} file(s) uploaded successfully!`);
    } catch (error) {
      toast.error("Error processing files");
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const handleSelectedSheetSelect = (selectedSheet, accountDetails) => {
    setSelectedSheet(selectedSheet);
    setAccountDetails(accountDetails);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.ms-excel.sheet.macroEnabled.12": [".xlsm"],
    },
    multiple: false,
    maxSize: 50 * 1024 * 1024, // 50MB
  });

  const handleSubmit = async () => {
    if (file.length === 0) {
      toast.warning("Please upload at least one file");
      return;
    }

    if (selectedSheet.length === 0) {
      toast.warning("Please select at least one sheet");
      return;
    }

    // Validate required fields
    const hasIncompleteAccounts = Object.values(accountDetails).some(
      (account) => !account.customerName || !account.machShipCompanyCode
    );

    if (hasIncompleteAccounts) {
      toast.error(
        "Please fill in all required fields (Customer Name and Machship Company Code)"
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      file.forEach((file) => formData.append("file", file.renamedFile));
      formData.append(
        "listSheets",
        JSON.stringify(selectedSheet.map((o) => o.value))
      );

      const payload = Object.entries(accountDetails).map(
        ([accountName, details]) => ({
          accountName,
          ...details,
        })
      );
      formData.append("accountDetails", JSON.stringify(payload));

      const response = await apiClient.post(
        `/api/v1/CustomerRatecardGenerator/upload`,
        formData
      );

      if (!response.ok) {
        let errorMessage = "Upload failed.";
        try {
          const errorResponse = await response.json();
          if (errorResponse.detail) {
            errorMessage = errorResponse.detail;
          } else if (errorResponse.error) {
            errorMessage = errorResponse.error;
          }
        } catch (parseError) {
          console.error("Failed to parse error response", parseError);
        }
        toast.error(errorMessage);
        return;
      }

      const now = new Date();
      const timestamp = now
        .toISOString()
        .replace(/[-:]/g, "")
        .replace("T", "_")
        .split(".")[0];

      const contentType = response.headers.get("Content-Type");

      if (contentType && contentType.includes("application/zip")) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        link.setAttribute("download", `Customer_Facing_${timestamp}.zip`);

        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(url);

        toast.success("Files converted and downloaded successfully!");
      } else {
        const errorText = await response.text();
        console.error("Download failed. Response is not ZIP:", errorText);
        toast.error("Upload succeeded, but server returned unexpected format.");
      }
    } catch (err) {
      console.error("Upload error:", err);
      if (!err.message.includes("Upload failed.")) {
        toast.error("Upload failed. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const removeFile = (name) => {
    setFile((prev) => prev.filter((file) => file.renamedFile.name !== name));
    toast.info("File removed");
  };

  const clearAllFiles = () => {
    setFile([]);
    setSelectedSheet([]);
    setAccountDetails({});
    toast.info("All files cleared");
  };

  const getValidationStatus = () => {
    if (file.length === 0)
      return { status: "empty", message: "No files uploaded" };
    if (selectedSheet.length === 0)
      return { status: "warning", message: "No sheets selected" };

    const accountCount = Object.keys(accountDetails).length;
    const hasIncompleteAccounts = Object.values(accountDetails).some(
      (account) => !account.customerName || !account.machShipCompanyCode
    );

    if (hasIncompleteAccounts) {
      return {
        status: "error",
        message: "Missing required fields in account details",
      };
    }

    if (accountCount > 0) {
      return {
        status: "success",
        message: `Ready to convert ${accountCount} account(s)`,
      };
    }

    return { status: "warning", message: "Configure account details" };
  };

  const validationStatus = getValidationStatus();
  const canSubmit = validationStatus.status === "success" && !isSubmitting;

  return (
    <>
      {isSubmitting && (
        <OverlayLoading
          isVisible={isSubmitting}
          message="Processing your request..."
          icon="truck"
        />
      )}

      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Header */}
          <div className="mb-8">
            <Title title="Customer Ratecard Generator" />
            <p className="text-gray-600 mt-2 text-sm sm:text-base">
              Upload Excel files and configure account details to generate
              customer ratecard
            </p>
          </div>

          {/* Status Banner */}
          {file.length > 0 && (
            <div
              className={`mb-6 p-4 rounded-lg border-l-4 ${
                validationStatus.status === "success"
                  ? "bg-green-50 border-green-400"
                  : validationStatus.status === "error"
                  ? "bg-red-50 border-red-400"
                  : validationStatus.status === "warning"
                  ? "bg-yellow-50 border-yellow-400"
                  : "bg-blue-50 border-blue-400"
              }`}
            >
              <div className="flex items-center">
                <FontAwesomeIcon
                  icon={
                    validationStatus.status === "success"
                      ? faCheckCircle
                      : validationStatus.status === "error"
                      ? faExclamationTriangle
                      : faInfoCircle
                  }
                  className={`w-5 h-5 mr-3 ${
                    validationStatus.status === "success"
                      ? "text-green-600"
                      : validationStatus.status === "error"
                      ? "text-red-600"
                      : validationStatus.status === "warning"
                      ? "text-yellow-600"
                      : "text-blue-600"
                  }`}
                />
                <p
                  className={`text-sm font-medium ${
                    validationStatus.status === "success"
                      ? "text-green-800"
                      : validationStatus.status === "error"
                      ? "text-red-800"
                      : validationStatus.status === "warning"
                      ? "text-yellow-800"
                      : "text-blue-800"
                  }`}
                >
                  {validationStatus.message}
                </p>
              </div>
            </div>
          )}

          {/* Upload Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
            <div className="p-6">
              <div className="mb-4">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">
                  Upload Files
                </h2>
                <p className="text-sm text-gray-600">
                  Supported formats: Excel (.xlsx, .xls, .xslm) files up to 50MB
                  each
                </p>
              </div>

              <DropzoneComponent
                getInputProps={getInputProps}
                getRootProps={getRootProps}
                isDragActive={isDragActive}
                fileTypes="Excel files"
              />
            </div>
          </div>

          {/* File List */}
          {file.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
              <div className="p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Uploaded Files ({file.length})
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Configure sheet selection and account details for each
                      file
                    </p>
                  </div>

                  <button
                    onClick={clearAllFiles}
                    className="flex items-center px-3 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <FontAwesomeIcon icon={faTrash} className="w-4 h-4 mr-2" />
                    Clear All
                  </button>
                </div>

                <div className="space-y-4">
                  {file.map((file) => (
                    <UploadedItemCard
                      key={file.renamedFile.name}
                      file={file.renamedFile}
                      sheetList={file.sheetList}
                      onDelete={removeFile}
                      onUpdateSelectedSheet={handleSelectedSheetSelect}
                      setIsSubmitting={setIsSubmitting}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Action Section */}
          {file.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="space-y-1">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Ready to Convert
                    </h3>
                    <p className="text-sm text-gray-600">
                      {Object.keys(accountDetails).length > 0
                        ? `${
                            Object.keys(accountDetails).length
                          } account(s) configured across ${
                            selectedSheet.length
                          } sheet(s)`
                        : "Configure account details to proceed"}
                    </p>
                  </div>

                  <button
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    className={`flex items-center px-6 py-3 rounded-lg font-medium text-sm transition-all duration-200 shadow-sm ${
                      canSubmit
                        ? "bg-blue-600 hover:bg-blue-700 text-white hover:shadow-md transform hover:-translate-y-0.5"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Processing...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon
                          icon={faDownload}
                          className="w-4 h-4 mr-2"
                        />
                        Convert & Download
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CustomerRateGenerator;
